import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { AdminUser } from '../types';
import { authApi } from '../api/auth';

interface AuthContextValue {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<AdminUser | null>;
  setCurrentAdmin: (admin: AdminUser) => void;
  /** Whether the current admin holds a permission (wildcard "*" grants all).
   *  No permission argument → always true. */
  can: (permission?: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedAdmin = localStorage.getItem('admin_user');
    const token = localStorage.getItem('admin_access_token');
    if (storedAdmin && token) {
      try {
        setAdmin(JSON.parse(storedAdmin));
      } catch {
        localStorage.clear();
      }
      // Refresh in the background so permissions reflect any role edits made
      // since this session's token was issued.
      authApi
        .me()
        .then((fresh) => {
          localStorage.setItem('admin_user', JSON.stringify(fresh));
          setAdmin(fresh);
        })
        .catch(() => {
          /* token invalid/expired — interceptor handles logout */
        });
    }
    setIsLoading(false);
  }, []);

  const can = (permission?: string) => {
    if (!permission) return true;
    const perms = admin?.permissions ?? [];
    return perms.includes('*') || perms.includes(permission);
  };

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    localStorage.setItem('admin_access_token', response.accessToken);
    localStorage.setItem('admin_refresh_token', response.refreshToken);
    localStorage.setItem('admin_user', JSON.stringify(response.admin));
    setAdmin(response.admin);
  };

  const setCurrentAdmin = (nextAdmin: AdminUser) => {
    localStorage.setItem('admin_user', JSON.stringify(nextAdmin));
    setAdmin(nextAdmin);
  };

  const refreshAdmin = async () => {
    const token = localStorage.getItem('admin_access_token');
    if (!token) return null;
    const nextAdmin = await authApi.me();
    setCurrentAdmin(nextAdmin);
    return nextAdmin;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('admin_refresh_token');
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // ignore — clear tokens regardless
      }
    }
    localStorage.clear();
    setAdmin(null);
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        isAuthenticated: !!admin,
        isLoading,
        login,
        logout,
        refreshAdmin,
        setCurrentAdmin,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
